import React from "react";
import {
  KeyRound,
  Users,
  HardHat,
  Scale,
  Building2,
  ShieldAlert,
  ArrowUpRight,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";

export const SERVICES = [
  {
    slug: "key-person",
    title: "Key Person Insurance",
    blurb:
      "Coverage on the founder, owner, or critical employee whose absence would put the business at risk — paid to the company to cover lost revenue, recruiting, and continuity.",
    icon: KeyRound,
    span: "lg:col-span-7 lg:row-span-2",
    tone: "primary",
    stat: "$250K – $10M typical face value · Funded buy-sell agreements available",
  },
  {
    slug: "group-life",
    title: "Group Life & Disability",
    blurb:
      "Voluntary and employer-paid term life, AD&D, short- and long-term disability.",
    icon: Users,
    span: "lg:col-span-5",
    tone: "surface",
    extras: ["Critical Illness", "Supplemental Health"],
    stat: "Up to 5× salary, no medical underwriting",
  },
  {
    slug: "workers-comp",
    title: "Workers' Compensation",
    blurb:
      "State-compliant comp programs with return-to-work consulting and safety credits.",
    icon: HardHat,
    span: "lg:col-span-5",
    tone: "surface",
    stat: "Class-code optimization included",
  },
  {
    slug: "liability",
    title: "General & Professional Liability",
    blurb:
      "CGL, E&O, and umbrella towers structured to your contractual obligations.",
    icon: Scale,
    span: "lg:col-span-4",
    tone: "accent",
  },
  {
    slug: "property",
    title: "Commercial Property",
    blurb:
      "Buildings, contents, BI/EE, and inland marine — replacement-cost valuations.",
    icon: Building2,
    span: "lg:col-span-4",
    tone: "surface",
  },
  {
    slug: "cyber",
    title: "Cyber & Privacy",
    blurb:
      "First- and third-party cyber, ransomware response, and PII/PHI breach response.",
    icon: ShieldAlert,
    span: "lg:col-span-4",
    tone: "surface",
  },
];

function toneClass(tone) {
  switch (tone) {
    case "primary":
      return "bg-primary text-primary-foreground border-primary";
    case "accent":
      return "bg-accent text-accent-foreground border-accent";
    default:
      return "bg-card text-foreground border-border";
  }
}

function iconWrap(tone) {
  switch (tone) {
    case "primary":
      return "bg-primary-foreground/10 text-primary-foreground";
    case "accent":
      return "bg-accent-foreground/15 text-accent-foreground";
    default:
      return "bg-secondary text-primary";
  }
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid={LANDING.services}
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
        <div className="lg:col-span-5">
          <p className="label-eyebrow mb-4"><span className="text-accent">02</span> — Coverage</p>
          <h2 className="font-display text-4xl md:text-5xl h-tight font-medium text-foreground">
            Six coverage lines.{" "}
            <span className="italic font-light text-accent">One broker</span> who reads the
            policy with you.
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-end">
          <p className="text-base md:text-lg text-muted-foreground max-w-xl">
            Most brokers sell what their top carrier is paying out on this
            quarter. We build the program backwards from your actual risk —
            then run it to market across 40+ A-rated carriers.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 auto-rows-[minmax(220px,auto)]">
        {SERVICES.map((s) => {
          const Icon = s.icon;
          return (
            <article
              key={s.slug}
              data-testid={LANDING.serviceCard(s.slug)}
              className={`group relative rounded-md border ${toneClass(
                s.tone
              )} ${s.span} p-7 md:p-9 flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 hover:shadow-lg shadow-sm overflow-hidden`}
            >
              <div className="flex items-start justify-between">
                <div
                  className={`h-12 w-12 rounded-md flex items-center justify-center ${iconWrap(
                    s.tone
                  )}`}
                >
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <ArrowUpRight
                  size={22}
                  className="opacity-40 group-hover:opacity-100 group-hover:rotate-12 transition-all duration-500"
                />
              </div>
              <div className="mt-12">
                <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
                  {s.title}
                </h3>
                <p
                  className={`mt-3 text-sm md:text-base max-w-md ${
                    s.tone === "primary" || s.tone === "accent"
                      ? ""
                      : "text-muted-foreground"
                  }`}
                  style={{
                    color:
                      s.tone === "primary" || s.tone === "accent"
                        ? "rgba(255,255,255,0.85)"
                        : undefined,
                  }}
                >
                  {s.blurb}
                </p>
                {s.extras && s.extras.length > 0 && (
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {s.extras.map((ex) => (
                      <li
                        key={ex}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                          s.tone === "primary" || s.tone === "accent"
                            ? "border-white/30 text-white/90"
                            : "border-border text-foreground bg-secondary/60"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            s.tone === "primary" || s.tone === "accent"
                              ? "bg-accent"
                              : "bg-accent"
                          }`}
                        />
                        {ex}
                      </li>
                    ))}
                  </ul>
                )}
                {s.stat && (
                  <p
                    className={`mt-6 text-xs uppercase tracking-[0.2em] font-semibold ${
                      s.tone === "primary"
                        ? "text-accent"
                        : s.tone === "accent"
                        ? "text-primary"
                        : "text-primary"
                    }`}
                  >
                    {s.stat}
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
